import { z } from 'zod';
import { dateTimeSchema, suretyAccountStatusSchema, contractSuretyAccountUnderwritingModelSizeSchema, closedReasonSchema, CLOSED_REASON_NOTE_MAX_LENGTH } from '../../../enums.mjs';
import '../../../types/actor/actor.mjs';
import '../../../types/address/address.mjs';
import '../../../types/credit-report/company-credit-report.mjs';
import '../../../types/credit-report/individual-credit-report.mjs';
import '../../../types/currency/currency.mjs';
import '../../../types/entityRelation/entity-relation.mjs';
import '../../../types/financial-formula/adjustable-value.mjs';
import '../../../types/financial-formula/adjustable-values-update.mjs';
import '../../../types/financial-formula/adjustment.mjs';
import '../../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../../types/is-encrypted.mjs';
import '../../../types/naics-codes/naics-codes.mjs';
import { naicsCodeId, companyContactId, agencyId, suretyAccountId, suretyIndemnityAgreementId, userId, lastName, firstName } from '../../../types/opaque.mjs';
import { Metadata as Metadata$1 } from '../../../types/surety/account/surety-account.types.mjs';
import '../../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../../types/surety/bond/surety-bond.types.mjs';
import '../../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../../types/surety/contract/contract-extraction-output.mjs';
import '../../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import { baseProfitabilitySchema, aiMediumFinancialSchema, aiLargeFinancialSchema } from '../../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../../types/surety/quote/surety-quote.types.mjs';
import '../../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../../types/user/user.types.mjs';
import { currencySchema, attachmentSchema, uploadedFileSchema, financialStatementFileSchema } from '../../../types/utils.mjs';
import { Metadata as Metadata$2 } from '../../../types/surety/account/contract/contract-surety-account.types.mjs';
import { validateCloseRequiredFields } from '../../../utils/validate-close-required-fields.util.mjs';
import { Create as Create$2 } from '../../company/company.dto.mjs';
import { Create as Create$1 } from '../../individual/individual.dto.mjs';
import { Create as Create$3 } from '../quote/surety-quote.dto.mjs';
import * as bondingLines_dto from './bonding-line/bonding-lines.dto.mjs';
export { bondingLines_dto as BondingLines };
import * as suretyAccountBondingLine_dto from './bonding-line/surety-account-bonding-line.dto.mjs';
export { suretyAccountBondingLine_dto as BondingLine };
import * as exposure_dto from './exposure/exposure.dto.mjs';
export { exposure_dto as Exposure };
import * as suretyAccountIndemnityAgreement_dto from './indemnity-agreement/surety-account-indemnity-agreement.dto.mjs';
export { suretyAccountIndemnityAgreement_dto as IndemnityAgreement };
import * as suretyAccountPreview_dto from './preview/surety-account-preview.dto.mjs';
export { suretyAccountPreview_dto as Preview };
import * as suretyAccountUnderwriting_dto from './underwriting/surety-account-underwriting.dto.mjs';
export { suretyAccountUnderwriting_dto as Underwriting };

const accountNaicsCodesSchema = z.array(naicsCodeId).min(1, "Required");
const baseContractHistorySchema = z.object({
    largestProjectCompleted: currencySchema.nullish().meta({ partial: true }),
    failedToCompleteAProject: z.boolean().nullish().meta({ partial: true }),
    lostAPaymentSuit: z.boolean().nullish().meta({ partial: true }),
    causedSuretyLoss: z.boolean().nullish().meta({ partial: true }),
});
const mediumContractHistorySchema = z.object({
    constructionProjectsGrossLossGt10PercentLast36Months: z
        .boolean()
        .nullish()
        .meta({ partial: true }),
    currentConstructionProjectExpectedGrossLoss: z.boolean().nullish().meta({ partial: true }),
});
const fullContractHistorySchema = baseContractHistorySchema.extend(mediumContractHistorySchema.shape);
const baseAdditionalInfoSchema = z.object({
    attachments: z.array(attachmentSchema()),
});
const smallAccountSchema = z.object({
    modelSize: z.literal("small"),
    naicsCodes: accountNaicsCodesSchema,
    history: baseContractHistorySchema,
    additionalInfo: baseAdditionalInfoSchema,
});
const mediumAccountSchema = z.object({
    modelSize: z.literal("medium"),
    naicsCodes: accountNaicsCodesSchema,
    history: fullContractHistorySchema,
    additionalInfo: baseAdditionalInfoSchema,
});
const largeAccountSchema = z.object({
    modelSize: z.literal("large"),
    naicsCodes: accountNaicsCodesSchema,
    history: fullContractHistorySchema,
    additionalInfo: baseAdditionalInfoSchema,
});
var History;
(function (History) {
    History.smallSchema = baseContractHistorySchema;
    History.mediumSchema = fullContractHistorySchema;
    History.largeSchema = fullContractHistorySchema;
})(History || (History = {}));
const metadataSchema = Metadata$1.schema
    .pick({ causedSuretyLoss: true })
    .extend({
    contract: Metadata$2.schema.omit({ bondingLine: true }).nullish(),
});
var Metadata;
(function (Metadata) {
    (function (Get) {
        (function (Response) {
            Response.schema = metadataSchema;
        })(Get.Response || (Get.Response = {}));
    })(Metadata.Get || (Metadata.Get = {}));
})(Metadata || (Metadata = {}));
var Get;
(function (Get) {
    (function (Response) {
        Response.schema = z.object({
            id: suretyAccountId,
            agencyId,
            status: suretyAccountStatusSchema,
            displayName: z.string(),
            primaryCompanyId: companyContactId,
            underwriter: z.object({
                id: userId,
                firstName: firstName.optional(),
                lastName: lastName.optional(),
            }),
            isPrequal: z.boolean(),
            isContractSuretyAccount: z.boolean(),
            modelSize: contractSuretyAccountUnderwritingModelSizeSchema.optional(),
            indemnityAgreementId: suretyIndemnityAgreementId.optional(),
            closedReason: z.string().optional(),
            closedNote: z.string().optional(),
            updatedAt: dateTimeSchema,
        });
    })(Get.Response || (Get.Response = {}));
})(Get || (Get = {}));
var Create;
(function (Create) {
    (function (Request) {
        Request.detailsSchema = z.object({
            companies: z
                .array(Create$2.FromSubmission.Request.schema, {
                error: "Please list all companies in this account",
            })
                .min(1, "Please list all companies in this account")
                .superRefine((val, ctx) => {
                if (!val.length)
                    return;
                const numPrimaryCompanies = val.reduce((acc, currentCompany) => {
                    if (currentCompany.isPrimaryCompany === true) {
                        return acc + 1;
                    }
                    return acc;
                }, 0);
                if (numPrimaryCompanies > 1) {
                    ctx.addIssue({
                        code: "custom",
                        input: val,
                        path: [],
                        message: "Only one primary company can be selected",
                    });
                }
                if (numPrimaryCompanies === 0) {
                    ctx.addIssue({
                        code: "custom",
                        input: val,
                        path: [],
                        message: "One company must be selected as the primary company",
                    });
                }
                const companyNames = val.map((c) => c.name.trim().toLowerCase());
                const duplicateNames = companyNames.filter((name, index) => companyNames.indexOf(name) !== index);
                if (duplicateNames.length > 0) {
                    ctx.addIssue({
                        code: "custom",
                        input: val,
                        path: [],
                        message: "Company names must be unique",
                    });
                }
            }),
            individuals: z.array(Create$1.FromSubmission.Request.schema).meta({ partial: true }),
        });
        const submitAdditionalInfoSchema = z.object({
            comment: z.string().nullish(),
            attachments: z.array(uploadedFileSchema()),
        });
        Request.aiSmallAccountSubmitSchema = smallAccountSchema.extend({
            requiresPaymentAndPerformanceBonds: z.boolean().nullish(),
            financials: z.object({
                profitability: baseProfitabilitySchema.nullish(),
                files: z.array(financialStatementFileSchema).default([]),
            }),
            additionalInfo: submitAdditionalInfoSchema,
            details: Request.detailsSchema,
        });
        Request.aiMediumAccountSubmitSchema = mediumAccountSchema.extend({
            requiresPaymentAndPerformanceBonds: z.boolean().nullish(),
            financials: aiMediumFinancialSchema,
            additionalInfo: submitAdditionalInfoSchema,
            details: Request.detailsSchema,
        });
        Request.aiLargeAccountSubmitSchema = largeAccountSchema.extend({
            requiresPaymentAndPerformanceBonds: z.boolean().nullish(),
            financials: aiLargeFinancialSchema,
            additionalInfo: submitAdditionalInfoSchema,
            details: Request.detailsSchema,
        });
        Request.aiAccountSchema = z.discriminatedUnion("modelSize", [
            Request.aiSmallAccountSubmitSchema,
            Request.aiMediumAccountSubmitSchema,
            Request.aiLargeAccountSubmitSchema,
        ]);
        Request.schema = z.object({
            aiEnabled: z.literal(true),
            account: Request.aiAccountSchema,
            quote: Create$3.Request.quoteSchemaForAccountCreation.optional(),
        });
    })(Create.Request || (Create.Request = {}));
    (function (Response) {
        Response.schema = z.object({
            id: suretyAccountId,
            status: suretyAccountStatusSchema,
        });
    })(Create.Response || (Create.Response = {}));
})(Create || (Create = {}));
var Update;
(function (Update) {
    (function (History) {
        (function (Request) {
            Request.schema = fullContractHistorySchema.partial().extend({
                updatedAt: dateTimeSchema,
            });
        })(History.Request || (History.Request = {}));
    })(Update.History || (Update.History = {}));
})(Update || (Update = {}));
var Close;
(function (Close) {
    (function (Request) {
        Request.schema = z
            .object({
            closedReason: closedReasonSchema,
            note: z.string().max(CLOSED_REASON_NOTE_MAX_LENGTH).optional(),
        })
            .superRefine(validateCloseRequiredFields);
    })(Close.Request || (Close.Request = {}));
})(Close || (Close = {}));

export { Close, Create, Get, History, Metadata, Update, accountNaicsCodesSchema, baseContractHistorySchema, fullContractHistorySchema, mediumContractHistorySchema, metadataSchema };
